import React, { useState, useRef, useEffect } from 'react';
import { Smile } from 'lucide-react';
import { REACTION_TYPES, REACTION_BY_TYPE } from '@/lib/reactions';

/**
 * ReactionBar — pickable emoji row + condensed summary chip.
 *
 *   <ReactionBar
 *     myReaction={post.my_reaction}
 *     counts={post.reaction_counts}
 *     onReact={(type) => api.react(post.id, type)}
 *     testIdPrefix={`post-${post.post_id}`}
 *     size="sm"
 *   />
 *
 * - When the user already reacted, click on their reaction toggles it off.
 * - Clicking the trigger ("React"/emoji) reveals the full picker.
 * - The picker auto-closes when the user clicks outside or picks something.
 */
export default function ReactionBar({
  myReaction,
  counts = {},
  totalOverride,
  onReact,
  testIdPrefix = 'reactions',
  size = 'sm',
  align = 'left',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const mine = myReaction ? REACTION_BY_TYPE[myReaction] : null;
  const total = typeof totalOverride === 'number'
    ? totalOverride
    : Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  const handlePick = (type) => {
    setOpen(false);
    onReact?.(type);
  };

  const iconSize = size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const triggerText = size === 'xs' ? 'text-[11px]' : 'text-xs';

  return (
    <div className={`relative inline-flex items-center gap-2 ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`${testIdPrefix}-trigger`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${triggerText} ${
          mine ? 'text-brand-navy font-semibold bg-brand-navy/5' : 'text-slate-400 hover:text-brand-navy hover:bg-brand-navy/5'
        }`}
      >
        {mine ? <span>{mine.emoji}</span> : <Smile className={iconSize} />}
        <span>{mine ? mine.label : 'React'}</span>
      </button>

      {/* Aggregate count + top-3 emojis chip */}
      {total > 0 && (
        <span
          data-testid={`${testIdPrefix}-summary`}
          className={`inline-flex items-center gap-1 ${triggerText} text-slate-500`}
        >
          {Object.entries(counts)
            .filter(([, c]) => c > 0)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([type]) => (
              <span key={type}>{REACTION_BY_TYPE[type]?.emoji}</span>
            ))}
          <span>{total}</span>
        </span>
      )}

      {open && (
        <div
          data-testid={`${testIdPrefix}-popup`}
          className={`absolute -top-10 ${align === 'right' ? 'right-0' : 'left-0'} bg-white border border-slate-200 rounded-full px-2 py-1 flex gap-1 shadow-md z-30`}
        >
          {REACTION_TYPES.map(r => (
            <button
              key={r.type}
              type="button"
              onClick={() => handlePick(r.type)}
              data-testid={`${testIdPrefix}-pick-${r.type}`}
              title={r.label}
              className="text-lg hover:scale-125 transition-transform"
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
